import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './notifications.module.scss';
import {
  getPushCapability,
  isPushRegisteredLocally,
  registerPushDevice,
} from '../../services/push-notification-service';
import { sendTestPushNotification } from '../../services/apiService';
import { useUser } from '../login/UserContext';

/**
 * Slim enable-push prompt at the top of the notification panel — the one
 * place every notification user already looks, on desktop and mobile.
 *
 * Rules of engagement:
 *  - Renders nothing when push can't work here (unsupported browser, no
 *    VAPID key configured, permission hard-denied) or is already enabled.
 *  - The browser permission dialog only ever fires from the Enable tap —
 *    never on mount (see push-notification-service).
 *  - iOS browser tabs get Add-to-Home-Screen guidance instead of a doomed
 *    permission prompt.
 *  - One shot per account on this device: the first time it renders for a
 *    signed-in user it is marked seen, so closing the panel, Not now, Enable
 *    or Done all retire it for good. Signed-out viewers never see it.
 */

const SEEN_STORAGE_PREFIX = 'bb-push-prompt-seen:';
/** Pre-one-shot snooze key. Anyone who already dismissed it counts as seen. */
const LEGACY_SNOOZE_KEY = 'bb-push-prompt-snooze-until';

function hasSeen(userId: number): boolean {
  try {
    return (
      !!localStorage.getItem(SEEN_STORAGE_PREFIX + userId) ||
      !!localStorage.getItem(LEGACY_SNOOZE_KEY)
    );
  } catch {
    // Storage blocked: we can't remember a dismissal, so don't nag at all.
    return true;
  }
}

function markSeen(userId: number) {
  try {
    localStorage.setItem(SEEN_STORAGE_PREFIX + userId, String(Date.now()));
  } catch {
    /* private mode — hasSeen already fails closed */
  }
}

type Phase = 'idle' | 'busy' | 'enabled' | 'error';

const PushEnableBanner: React.FC = () => {
  // Capability is computed once per panel open (component mount) — it only
  // changes via user action (permission grant), which we handle explicitly.
  const { user } = useUser();
  const userId = user.id;
  const capability = useMemo(() => getPushCapability(), []);
  const [phase, setPhase] = useState<Phase>('idle');
  // Decided once per panel open: a banner that is showing stays put until the
  // panel closes, even though it was marked seen the moment it appeared.
  const [hidden, setHidden] = useState(
    () => !(userId > 0) || hasSeen(userId) || isPushRegisteredLocally(),
  );
  const [testSent, setTestSent] = useState(false);

  const visible =
    !hidden &&
    (phase === 'enabled' ||
      capability.reason === 'ios-requires-install' ||
      capability.supported);

  useEffect(() => {
    if (visible) markSeen(userId);
  }, [visible, userId]);

  const handleEnable = useCallback(async () => {
    setPhase('busy');
    const result = await registerPushDevice();
    if (result.ok) {
      setPhase('enabled');
    } else if (result.reason === 'permission-denied' || result.reason === 'permission-default') {
      // User declined the browser dialog — treat like "Not now".
      setHidden(true);
    } else {
      setPhase('error');
    }
  }, []);

  const handleDismiss = useCallback(() => {
    setHidden(true);
  }, []);

  const handleSendTest = useCallback(async () => {
    setTestSent(true);
    await sendTestPushNotification();
  }, []);

  if (!visible) return null;

  // Enabled just now — confirm and offer a real end-to-end test push.
  if (phase === 'enabled') {
    return (
      <div className={styles.pushBanner} role="status">
        <span className={styles.pushBannerText}>
          Notifications enabled on this device.
        </span>
        <div className={styles.pushBannerActions}>
          <button
            className={styles.pushBannerBtn}
            onClick={handleSendTest}
            disabled={testSent}
          >
            {testSent ? 'Test sent' : 'Send test'}
          </button>
          <button className={styles.pushBannerDismiss} onClick={() => setHidden(true)}>
            Done
          </button>
        </div>
      </div>
    );
  }

  if (capability.reason === 'ios-requires-install') {
    return (
      <div className={styles.pushBanner}>
        <span className={styles.pushBannerText}>
          Want push notifications? Add BookieBrawler to your Home Screen
          (Share&nbsp;→&nbsp;Add to Home Screen), then enable them from the app.
        </span>
        <div className={styles.pushBannerActions}>
          <button className={styles.pushBannerDismiss} onClick={handleDismiss}>
            Not now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pushBanner}>
      <span className={styles.pushBannerText}>
        {phase === 'error'
          ? "Couldn't enable notifications — try again."
          : 'Get bet updates on this device, even when the tab is closed.'}
      </span>
      <div className={styles.pushBannerActions}>
        <button
          className={styles.pushBannerBtn}
          onClick={handleEnable}
          disabled={phase === 'busy'}
        >
          {phase === 'busy' ? 'Enabling…' : phase === 'error' ? 'Retry' : 'Enable'}
        </button>
        <button className={styles.pushBannerDismiss} onClick={handleDismiss}>
          Not now
        </button>
      </div>
    </div>
  );
};

export default PushEnableBanner;
